import axios from 'axios'
import { API_URL } from '../utils/apiConfig'

const api = axios.create({
    baseURL: API_URL,
    headers: {
        'Accept': 'application/json',
    },
})

// Request interceptor to add auth token
api.interceptors.request.use((config) => {
    const token = localStorage.getItem('auth_token')
    if (token) {
        config.headers.Authorization = `Bearer ${token}`
    }

    // Allow Axios to set the multipart/form-data boundary automatically
    if (config.data instanceof FormData) {
        if (typeof config.headers.delete === 'function') {
            config.headers.delete('Content-Type')
        } else {
            delete config.headers['Content-Type']
        }
    }

    return config
})

// Response interceptor for error handling
api.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            localStorage.removeItem('auth_token')
            window.location.href = '/login'
        }
        return Promise.reject(error)
    }
)

// Auth
export const login = async (email, password) => {
    return api.post('/login', { email, password })
}
export const register = async (data) => {
    return api.post('/register', data)
}
export const logout = () => api.post('/logout')
export const getUser = () => api.get('/user')
export const getFacebookAuthUrl = () => api.get('/auth/facebook/redirect')

// Events
export const getEvents = (params) => api.get('/events', { params })
export const getEvent = (slug) => api.get(`/events/${slug}`)
export const getEventPerformances = (slug) => api.get(`/events/${slug}/performances`)

// Performances
export const getPerformance = (id) => api.get(`/performances/${id}`)
export const getPerformanceSeats = (id) => api.get(`/performances/${id}/seats`)
export const getPerformanceTicketTypes = (id) => api.get(`/performances/${id}/ticket-types`)

// Halls
export const getHalls = () => api.get('/halls')
export const getHall = (id) => api.get(`/halls/${id}`)

// Seats
export const lockSeats = (performanceId, seatIds) =>
    api.post('/seats/lock', { performance_id: performanceId, seat_ids: seatIds })
export const unlockSeats = (performanceId, seatIds) =>
    api.delete('/seats/lock', { data: { performance_id: performanceId, seat_ids: seatIds } })

// Orders
export const getOrders = () => api.get('/orders')
export const getOrder = (id) => api.get(`/orders/${id}`)
export const createOrder = (data) => api.post('/orders', data)
export const payOrder = (id) => api.post(`/orders/${id}/pay`)
export const cancelOrder = (id) => api.post(`/orders/${id}/cancel`)

// Promo Codes
export const validatePromoCode = (code, orderAmount) =>
    api.post('/promo-codes/validate', { code, order_amount: orderAmount })

// Tickets
export const getMyTickets = (params) => api.get('/tickets', { params })
export const downloadTicket = (id) =>
    api.get(`/tickets/${id}/download`, { responseType: 'blob' })

// Profile
export const getProfile = () => api.get('/profile')
export const updateProfile = (data) => api.put('/profile', data)
export const getProfileOrders = (params) => api.get('/profile/orders', { params })
export const getUpcomingEvents = () => api.get('/profile/upcoming-events')

// Addresses
export const getAddresses = () => api.get('/addresses')
export const createAddress = (data) => api.post('/addresses', data)
export const updateAddress = (id, data) => api.put(`/addresses/${id}`, data)
export const deleteAddress = (id) => api.delete(`/addresses/${id}`)
export const setDefaultAddress = (id) => api.post(`/addresses/${id}/default`)

// Newsletters
export const getNewsletters = () => api.get('/newsletters')
export const subscribeNewsletter = (slug) => api.post(`/newsletters/${slug}/subscribe`)
export const unsubscribeNewsletter = (slug) => api.delete(`/newsletters/${slug}/unsubscribe`)

// Admin Newsletters
export const adminGetNewsletters = () => api.get('/admin/newsletters')
export const adminUpdateNewsletter = (id, data) => api.put(`/admin/newsletters/${id}`, data)

// Cashier
export const adminCashierIssueTicket = (data) => api.post('/admin/cashier/issue-ticket', data)

// Help Center
export const getFaqs = () => api.get('/faqs')
export const submitContact = (data) => api.post('/contact', data)

// Admin APIs
export const adminGetEvents = (params) => api.get('/admin/events', { params })
export const adminGetEvent = (id) => api.get(`/admin/events/${id}`)
export const adminCreateEvent = (data) => api.post('/admin/events', data)
export const adminUpdateEvent = (id, data) => {
    // Laravel's explicit API route uses 'id', but actually the Route Model binding uses slug by default.
    // However in AdminEventController it's using 'Event $event', and Event Model defines getRouteKeyName() as 'slug'.
    // BUT we are forcing ID for admin if $event isn't matching. Wait, actually Event has getRouteKeyName() = 'slug'.
    // If we pass an ID, "No query results for model [Event] 4" means it's searching by slug for the value "4".

    // We should pass the actual SLUG instead of ID when hitting the API, or the backend expects the slug.
    if (data instanceof FormData) {
        data.append('_method', 'PUT')
        return api.post(`/admin/events/${id}`, data) // id here must be the slug
    }
    return api.put(`/admin/events/${id}`, data)
}
export const adminDeleteEvent = (id) => api.delete(`/admin/events/${id}`)

export const adminGetHalls = () => api.get('/admin/halls')
export const adminGetHall = (id) => api.get(`/admin/halls/${id}`)
export const adminCreateHall = (data) => api.post('/admin/halls', data)
export const adminUpdateHall = (id, data) => {
    if (data instanceof FormData) {
        data.append('_method', 'PUT')
        return api.post(`/admin/halls/${id}`, data)
    }
    return api.put(`/admin/halls/${id}`, data)
}
export const adminUpdateSeats = (hallId, seats) =>
    api.post(`/admin/halls/${hallId}/seats`, { seats })
export const adminGenerateHallLayout = (hallId, data) =>
    api.post(`/admin/halls/${hallId}/generate-layout`, data)
export const adminToggleHallActive = (hallId) =>
    api.post(`/admin/halls/${hallId}/toggle-active`)

// Admin Sector APIs
export const adminGetSectors = (hallId) =>
    api.get(`/admin/halls/${hallId}/sectors`)
export const adminGetSector = (hallId, sectorId) =>
    api.get(`/admin/halls/${hallId}/sectors/${sectorId}`)
export const adminCreateSector = (hallId, data) =>
    api.post(`/admin/halls/${hallId}/sectors`, data)
export const adminUpdateSector = (hallId, sectorId, data) =>
    api.put(`/admin/halls/${hallId}/sectors/${sectorId}`, data)
export const adminDeleteSector = (hallId, sectorId) =>
    api.delete(`/admin/halls/${hallId}/sectors/${sectorId}`)
export const adminUpdateSectorSeats = (hallId, sectorId, seats, isSymmetrical) =>
    api.post(`/admin/halls/${hallId}/sectors/${sectorId}/seats`, { seats, is_symmetrical: isSymmetrical })
export const adminGenerateSectorLayout = (hallId, sectorId, data) =>
    api.post(`/admin/halls/${hallId}/sectors/${sectorId}/generate-layout`, data)

export const adminGetPerformances = (params) => api.get('/admin/performances', { params })
export const adminCreatePerformance = (data) => api.post('/admin/performances', data)
export const adminUpdatePerformance = (id, data) => api.put(`/admin/performances/${id}`, data)
export const adminDeletePerformance = (id) => api.delete(`/admin/performances/${id}`)

export const adminGetPromoCodes = (params) => api.get('/admin/promo-codes', { params })
export const adminCreatePromoCode = (data) => api.post('/admin/promo-codes', data)
export const adminUpdatePromoCode = (id, data) => api.put(`/admin/promo-codes/${id}`, data)
export const adminDeletePromoCode = (id) => api.delete(`/admin/promo-codes/${id}`)

export const adminGetSalesAnalytics = (params) => api.get('/admin/analytics/sales', { params })
export const adminGetEventAnalytics = () => api.get('/admin/analytics/events')

// Admin FAQ Management
export const adminGetFaqs = () => api.get('/admin/faqs')
export const adminCreateFaqCategory = (data) => api.post('/admin/faq-categories', data)
export const adminUpdateFaqCategory = (id, data) => api.put(`/admin/faq-categories/${id}`, data)
export const adminDeleteFaqCategory = (id) => api.delete(`/admin/faq-categories/${id}`)
export const adminCreateFaq = (data) => api.post('/admin/faqs', data)
export const adminUpdateFaq = (id, data) => api.put(`/admin/faqs/${id}`, data)
export const adminDeleteFaq = (id) => api.delete(`/admin/faqs/${id}`)

// Admin Contact Messages & Tags
export const adminGetContactMessages = (params) => api.get('/admin/contact-messages', { params })
export const adminGetContactMessage = (id) => api.get(`/admin/contact-messages/${id}`)
export const adminUpdateContactMessage = (id, data) => api.put(`/admin/contact-messages/${id}`, data)
export const adminDeleteContactMessage = (id) => api.delete(`/admin/contact-messages/${id}`)

export const adminGetContactMessageTags = () => api.get('/admin/contact-message-tags')
export const adminCreateContactMessageTag = (data) => api.post('/admin/contact-message-tags', data)
export const adminUpdateContactMessageTag = (id, data) => api.put(`/admin/contact-message-tags/${id}`, data)
export const adminDeleteContactMessageTag = (id) => api.delete(`/admin/contact-message-tags/${id}`)

// Admin Orders
export const adminGetOrders = (params) => api.get('/admin/orders', { params })
export const adminGetOrder = (id) => api.get(`/admin/orders/${id}`)

// Admin Users  
export const adminGetUsers = (params) => api.get('/admin/users', { params })
export const adminGetRoles = () => api.get('/admin/users/roles')
export const adminGetUser = (id) => api.get(`/admin/users/${id}`)
export const adminUpdateUser = (id, data) => api.put(`/admin/users/${id}`, data)
export const adminToggleUserActive = (id) => api.post(`/admin/users/${id}/toggle-active`)

// Admin Reports
export const adminGetReportOverview = (params) => api.get('/admin/reports/overview', { params })
export const adminGetRevenueByHall = (params) => api.get('/admin/reports/revenue-by-hall', { params })
export const adminGetRevenueByEvent = (params) => api.get('/admin/reports/revenue-by-event', { params })
export const adminGetRevenueByCategory = (params) => api.get('/admin/reports/revenue-by-category', { params })

export default api
